/**
 * Liturgy and planning API client.
 *
 * The acts are separate calls to separate routes, deliberately mirroring the server. There is no
 * `updateItem(state)` helper here, because offering one would let a caller express "approve" as a
 * variation of "save" — the confusion the whole model exists to prevent.
 */

import type {
  EffectiveItemConfiguration,
  ItemValue,
  LifecycleState,
  DelegableAction,
} from '@pius/contracts';

import { request } from '../../api/client';

export { ApiError, request } from '../../api/client';

export interface LiturgySummary {
  id: string;
  title: string;
  lifecycle_state: LifecycleState;
  celebration_local_datetime: string | null;
  celebration_timezone: string | null;
  place: string | null;
  archived_at: string | null;
  updated_at: string;
  /** How this Account reaches this Liturgy. Places the row in a collection — never a permission. */
  access_via: 'owner' | 'delegation';
}

export type Blocker =
  | { kind: 'item'; item_id: string; label: string; explanation?: string }
  | { kind: 'role'; role_key: string; label: string; explanation?: string };

/**
 * `GET /liturgies/:id`.
 *
 * Derived from `LiturgySummary` by subtraction, and the subtraction is the point: this type once
 * claimed `updated_at` and `access_via` because the summary has them, and the detail route has
 * never returned either. Nothing rendered them, so nothing broke — a screen that had would have
 * drawn `undefined` with no gate objecting, since `openapi:check` describes no response bodies at
 * all. Only fields the controller actually writes belong here.
 */
export interface LiturgyDetail extends Omit<
  LiturgySummary,
  'archived_at' | 'updated_at' | 'access_via'
> {
  owner_name: string;
  is_planning_editable: boolean;
  /** Only Constraints appear here. An unset optional item is not a blocker. */
  blockers: Blocker[];
}

export interface PlanningItemView {
  id: string;
  key: string;
  label: string;
  item_type: string;
  ordinal: number;
  is_constraint: boolean;
  constraint_explanation: string | null;
  value: ItemValue | null;
  state: string;
  note: string | null;
  /** Distinct from `state`: a fresh draft can sit on top of an approved value. */
  is_approved: boolean;
  /**
   * What the effective Selection Policy offers for this Item, resolved from the pinned Template
   * Version. Null when no Template Version backs the workspace.
   *
   * The client uses it to render; it never relies on it to enforce. The same resolution runs on the
   * write path, and a `hidden` Item is not in this response at all
   * (docs/engineering/06-selection-policy.md).
   */
  configuration: EffectiveItemConfiguration | null;
}

export interface PlanningSectionView {
  id: string;
  key: string;
  label: string;
  ordinal: number;
  items: PlanningItemView[];
}

export interface PlanningView {
  workspace_id: string;
  template_version_id: string | null;
  /** Whether to offer the Selection Policy link, and whether `hidden` Items are in this response. */
  may_configure_policy: boolean;
  sections: PlanningSectionView[];
}

export function fetchLiturgies(): Promise<{ data: LiturgySummary[] }> {
  return request('/liturgies');
}

export function fetchLiturgy(id: string): Promise<LiturgyDetail> {
  return request(`/liturgies/${id}`);
}

export function fetchPlanning(id: string): Promise<PlanningView> {
  return request(`/liturgies/${id}/planning`);
}

export interface CreatedLiturgy {
  id: string;
  lifecycle_state: LifecycleState;
  /**
   * Present **only** when the celebration was created without an account, and only in this one
   * response. It is the anonymous creator's sole way back — there is no route that can return it
   * again, because only its fingerprint is stored.
   */
  administrator_link: { url: string; short_url: string; shown_once: true } | null;
}

export function createLiturgy(input: {
  title: string;
  template_version_id: string;
  celebration_local_datetime?: string | null;
  celebration_timezone?: string | null;
  place?: string | null;
}): Promise<CreatedLiturgy> {
  return request('/liturgies', { method: 'POST', body: JSON.stringify(input) });
}

// ── the four acts, as four calls ────────────────────────────────────────────

export function saveItem(
  itemId: string,
  value: ItemValue | null,
  note?: string,
): Promise<{ revision_id: string; state: string }> {
  return request(`/planning/items/${itemId}`, {
    method: 'POST',
    body: JSON.stringify({ value, note }),
  });
}

export function submitItem(itemId: string): Promise<{ state: string }> {
  return request(`/planning/items/${itemId}/submit`, { method: 'POST' });
}

export function approveItem(itemId: string): Promise<{ state: string; is_approved: boolean }> {
  return request(`/planning/items/${itemId}/approve`, { method: 'POST' });
}

export function requestRevision(itemId: string, reason: string): Promise<{ state: string }> {
  return request(`/planning/items/${itemId}/request-revision`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export function finalizeLiturgy(id: string): Promise<{ lifecycle_state: LifecycleState }> {
  return request(`/liturgies/${id}/finalize`, { method: 'POST' });
}

export function openPlanning(id: string): Promise<void> {
  return request(`/liturgies/${id}/open-planning`, { method: 'POST' });
}

// ── giving back your own access ─────────────────────────────────────────────

/**
 * End your own standing access to a Liturgy.
 *
 * The counterpart to being shared with, and not an "undo" of anything: sharing granted the access
 * immediately, so this is the only act that can take it away again from the person holding it. It
 * is **not reversible by the caller** — nobody can grant themselves access, so recovering it means
 * being shared with a second time. Callers must confirm before calling this.
 *
 * 204 whatever the outcome, including for a Liturgy the requester cannot see. That is deliberate on
 * the server: a 404 here would tell an unauthorized caller which Liturgy ids exist.
 */
export function renounceAccess(liturgyId: string): Promise<void> {
  return request(`/liturgies/${liturgyId}/collaborators/me`, { method: 'DELETE' });
}

/**
 * An Account holding standing access to a Liturgy.
 *
 * Deliberately **not** in `features/capabilities/api.ts`, next to `CapabilityRecord`. The two are
 * different mechanisms — a Capability is a bearer credential anyone holding the string may use, a
 * delegation is authority attached to an Account — and the server keeps them in separate tables,
 * separate services and separate routes. Filing them together in the client is the first step
 * towards one merged list in the UI, which is how somebody revokes the wrong one.
 *
 * `account_id` and nothing more human than that: planning has no port that turns an Account into a
 * name, by design. Anything that wants to show *who* needs identity to answer first.
 */
export interface Collaborator {
  account_id: string;
  /** What this Account may do here — already intersected server-side with the sharer's own. */
  scope: string[];
  created_at: string;
}

/** Requires `manage_access`; 404 for anyone who may not see the Liturgy at all. */
export function fetchCollaborators(liturgyId: string): Promise<{ data: Collaborator[] }> {
  return request(`/liturgies/${liturgyId}/collaborators`);
}

/**
 * Every action that may be handed to another Account, in the order a scope picker should offer it.
 *
 * A mirror of the server's `DELEGABLE_ACTIONS`, and deliberately an **allowlist** rather than the
 * two-element `NON_DELEGABLE_ACTIONS` it complements. If the vocabulary grows and nobody updates
 * this file, an allowlist declines to offer the new action; a denylist would offer it by default,
 * and "the list we forgot to update" must fail towards granting less.
 *
 * Nothing here is a permission check. `share()` intersects the requested scope with the sharer's
 * own authority server-side and stores the result, so this list only decides what a screen is
 * willing to *ask* for. The screen narrows further, to what the sharer actually holds
 * (`fetchMyActions`), because offering a box the server will silently drop is a lie told by the UI.
 */

/**
 * What the signed-in requester may do on this Liturgy.
 *
 * The sharer's own authority is the upper bound on what they may delegate, and until this route
 * existed the client had no way to read it — so a scope picker either offered every delegable
 * action and let the server quietly drop the ones the sharer lacks, or invented a rule of its own.
 *
 * The response may include actions that are **not** delegable: `transfer_ownership` for an Owner,
 * `administer_site` for a Site Administrator. It answers "what may I do", not "what may I give",
 * so any caller building a scope picker must intersect with `DELEGABLE_ACTIONS` itself.
 */
export function fetchMyActions(liturgyId: string): Promise<{ data: string[] }> {
  return request(`/liturgies/${liturgyId}/my-actions`);
}

/**
 * Share this Liturgy with an email address.
 *
 * **The body field is `scope`, not `actions`.** The route validates it against the whole vocabulary
 * and refuses an unknown or non-delegable member outright rather than dropping it.
 *
 * **202, with a body that is identical whether or not that address belongs to an Account.** That is
 * not vagueness: reporting "delegated" versus "invited" would make this route an account-existence
 * oracle. Callers must not reconstruct the difference — one confirmation, the same words, the same
 * timing, for both.
 */
export function shareWithAccount(
  liturgyId: string,
  input: { email: string; scope: DelegableAction[] },
): Promise<{ accepted: true }> {
  return request(`/liturgies/${liturgyId}/collaborators`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Withdraw another Account's standing access.
 *
 * Distinct from `renounceAccess` above, which ends the caller's own. Idempotent server-side — a
 * second click succeeds — and it revokes rather than deletes, so what that Account contributed
 * stays exactly where it is. Callers must confirm before calling this.
 */
export function revokeCollaborator(liturgyId: string, accountId: string): Promise<void> {
  return request(`/liturgies/${liturgyId}/collaborators/${accountId}`, { method: 'DELETE' });
}

// ── account-wide readiness ──────────────────────────────────────────────────

export interface ReadinessItem {
  liturgy_id: string;
  title: string;
  unsatisfied_items: Array<{ id: string; label: string; explanation: string }>;
  unassigned_roles: Array<{ role_key: string; label: string }>;
}

export function fetchReadiness(): Promise<{ data: ReadinessItem[] }> {
  return request('/liturgies/readiness');
}
